import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, forwardRef, OnInit, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule, AbstractControl, NgControl } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { provideNativeDateAdapter } from '@angular/material/core';
import { FormErrorMessages } from '../form-error-messages';

@Component({
  selector: 'app-mat-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule
  ],
  providers: [
    provideNativeDateAdapter(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MatDatePickerComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mat-date-picker.component.html',
  styleUrls: ['./mat-date-picker.component.scss']
})
export class MatDatePickerComponent implements ControlValueAccessor, OnInit {
  private defaultControl = new FormControl<Date | null>(null);
  private _control: AbstractControl | null = null;

  constructor(private injector: Injector) {}

  @Input() set control(value: AbstractControl | null) {
    this._control = value || this.defaultControl;
    // Subscribe to value changes when control is set
    if (this._control) {
      this._control.valueChanges.subscribe(val => {
        if (val !== this.value) {
          this.value = val;
        }
      });
    }
  }
  get control(): FormControl {
    return (this._control as FormControl) || this.defaultControl;
  }

  @Input() label = 'Choose a date';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() appearance: 'fill' | 'outline' = 'outline';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;

  // Custom error message support
  @Input() errorMessage = '';
  @Input() fieldName = ''; // For FormErrorMessages lookup
  @Input() customErrorMessages: { [key: string]: string } = {};

  @Output() dateChange = new EventEmitter<Date | null>();

  value: Date | null = null;
  isInvalid = false;

  // ControlValueAccessor implementation
  private onChange = (_: any) => {};
  onTouched = () => {};

    writeValue(value: Date | null): void {
    this.value = value;
    // Update the internal value when the form control value changes
    if (this._control && value !== this._control.value) {
      this._control.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateChange(event: any): void {
    const date = event.value;
    this.value = date;
    this.onChange(date);
    this.onTouched();
    if (this._control) {
      this._control.setValue(date, { emitEvent: false });
    }
    this.dateChange.emit(date);
    this.updateErrorState();
  }

  getErrorMessage(): string {
    if (!this._control) return '';
    const errors = this._control.errors;
    if (!errors) return '';

    // Priority 1: Direct error message override
    if (this.errorMessage) {
      return this.errorMessage;
    }

    // Priority 2: Custom error messages for specific error types
    const errorKeys = Object.keys(errors);
    for (const errorKey of errorKeys) {
      if (this.customErrorMessages[errorKey]) {
        return this.customErrorMessages[errorKey];
      }
    }

    // Priority 3: FormErrorMessages lookup by field name
    if (this.fieldName) {
      const fieldErrors = FormErrorMessages.getFieldErrorMessages(this.fieldName);
      for (const errorKey of errorKeys) {
        if (fieldErrors[errorKey]) {
          return FormErrorMessages.getErrorMessage(this.fieldName, errorKey, errors[errorKey]);
        }
      }
    }

    // Priority 4: Common error messages
    for (const errorKey of errorKeys) {
      const commonError = FormErrorMessages.COMMON_ERRORS[errorKey];
      if (commonError) {
        // Use getErrorMessage with a generic field name for common errors
        return FormErrorMessages.getErrorMessage('date', errorKey, errors[errorKey]);
      }
    }

    // Priority 5: Default error message
    return 'Please select a valid date';
  }

  hasError(): boolean {
    const hasError = !!this._control && this._control.errors !== null && (this._control.dirty || this._control.touched);
    return hasError;
  }

  private updateErrorState() {
    if (this._control) {
      this.isInvalid = this._control.invalid && (this._control.dirty || this._control.touched);
      if (this.isInvalid) {
        this.errorMessage = this.getErrorMessage();
      }
    } else {
      this.isInvalid = !!this.errorMessage;
    }
  }

  ngOnInit() {
    // Try to get the control from NgControl if available
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (ngControl && ngControl.control) {
        this._control = ngControl.control;
        ngControl.valueAccessor = this;
      } else if (!this._control) {
        this._control = this.defaultControl;
      }
    } catch (error) {
      // If NgControl is not available, use default control
      if (!this._control) {
        this._control = this.defaultControl;
      }
    }
  }
}
